import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useOutletContext } from 'react-router-dom';
import axiosInstant from '../../../api';
import { getBrand } from '../../../api/endpoints';
import { IBrandDetailsContext } from '../SingleBrandDetails';
import { RmnSettings } from '../../../types/rmn';
import { resolveRmnPalette, contrastText } from '../../../utils/color';
import { portalBasePath } from '../../../utils/portalPath';

const HEX_RE = /^#[0-9a-fA-F]{3,8}$/;

interface ColorFieldProps {
    label: string;
    value: string;
    fallback: string;
    onChange: (value: string) => void;
}

// Native color input paired with a hex text field.
function ColorField({ label, value, fallback, onChange }: ColorFieldProps) {
    const pickerValue = HEX_RE.test(value) ? value : fallback;
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <input
                type="color"
                value={pickerValue.slice(0, 7)}
                onChange={(e) => onChange(e.target.value)}
                style={{ width: 40, height: 40, border: "none", background: "none", cursor: "pointer" }}
            />
            <TextField
                label={label}
                size="small"
                value={value}
                placeholder={fallback}
                onChange={(e) => onChange(e.target.value)}
                error={!!value && !HEX_RE.test(value)}
                helperText={!!value && !HEX_RE.test(value) ? "Use #RRGGBB" : undefined}
            />
        </Stack>
    );
}

// Blocked-domain copy (lib/brandDomains on the API).
const saveErrorMessage = (error: any): string => {
    const data = error?.response?.data;
    const code = typeof data === "string" ? data : data?.error;
    if (code === "WebsiteDomainNotAllowed") {
        return `${data?.domain || "That domain"} can't be added: it's a retailer, marketplace or shared host, or belongs to another network.`;
    }
    if (code === "InvalidAllowedDomains") return "One of the domains isn't valid.";
    return "Failed to save settings.";
};

interface WhiteLabelSettingsFormProps {
    brand: IBrandDetailsContext["brand"];
    setBrand: (brand: any) => void;
    // Internal staff (brand detail > Network) also edit the PDP-audit
    // guidance; brand admins (Settings > White Label) don't — the API
    // strips it for them.
    internal?: boolean;
}

// Editor for a brand's white-label portal settings: system name, tagline,
// colors, allowed domains, and (internal only) the PDP-audit prompt
// guidance. Saves via PATCH /brands/:id; the API sanitizes and, for brand
// admins, blocklist-checks newly added domains.
export function WhiteLabelSettingsForm({ brand, setBrand, internal = false }: WhiteLabelSettingsFormProps) {

    const settings: RmnSettings = brand.rmn_settings || {};
    const [systemName, setSystemName] = useState(settings.system_name || "");
    const [loginTagline, setLoginTagline] = useState(settings.login_tagline || "");
    const [bgColor, setBgColor] = useState(settings.bg_color || "");
    const [primaryColor, setPrimaryColor] = useState(settings.primary_color || "");
    const [secondaryColor, setSecondaryColor] = useState(settings.secondary_color || "");
    const [domains, setDomains] = useState<string[]>(brand.allowed_domains || []);
    const [domainInput, setDomainInput] = useState("");
    const [auditGuidance, setAuditGuidance] = useState(brand.audit_guidance || "");
    const [saving, setSaving] = useState(false);

    const draftSettings: RmnSettings = useMemo(
        () => ({
            system_name: systemName || undefined,
            login_tagline: loginTagline || undefined,
            bg_color: HEX_RE.test(bgColor) ? bgColor : undefined,
            primary_color: HEX_RE.test(primaryColor) ? primaryColor : undefined,
            secondary_color: HEX_RE.test(secondaryColor) ? secondaryColor : undefined,
        }),
        [systemName, loginTagline, bgColor, primaryColor, secondaryColor]
    );
    const preview = resolveRmnPalette(draftSettings, brand.name);

    const portalUrl = `${window.location.origin}${portalBasePath(brand.slug || '')}/login`;

    const addDomain = () => {
        const raw = domainInput
            .trim()
            .toLowerCase()
            .replace(/^[a-z]+:\/\//, "")
            .replace(/^www\./, "")
            .replace(/[/:?#].*$/, "");
        if (!raw) return;
        if (!domains.includes(raw)) setDomains([...domains, raw]);
        setDomainInput("");
    };

    const handleSave = async () => {
        const invalidColor = [bgColor, primaryColor, secondaryColor].some(
            (c) => c && !HEX_RE.test(c)
        );
        if (invalidColor) {
            toast.error("Fix invalid colors before saving.");
            return;
        }
        setSaving(true);
        try {
            const payload: Record<string, unknown> = {
                name: brand.name,
                rmn_settings: draftSettings,
                allowed_domains: domains,
                ...(internal ? { audit_guidance: auditGuidance || null } : {}),
            };
            const response = await axiosInstant.patch(getBrand(brand.id as number), payload);
            if (response?.data) setBrand(response.data);
            toast.success("White-label settings saved.");
        } catch (error) {
            toast.error(saveErrorMessage(error));
        }
        setSaving(false);
    };

    return (
        <Box className="flex flex-col lg:flex-row gap-6">
            <Stack spacing={3} className="flex-1 max-w-[480px]">
                <Typography variant="h6">White Label</Typography>
                <TextField
                    label="System name"
                    size="small"
                    value={systemName}
                    placeholder={`${brand.name} Commerce Content OS`}
                    onChange={(e) => setSystemName(e.target.value)}
                    inputProps={{ maxLength: 120 }}
                />
                <TextField
                    label="Login tagline"
                    size="small"
                    value={loginTagline}
                    placeholder="Retail media tools for your brand partners"
                    onChange={(e) => setLoginTagline(e.target.value)}
                    inputProps={{ maxLength: 120 }}
                />
                <ColorField label="Background" value={bgColor} fallback="#FAFAF8" onChange={setBgColor} />
                <ColorField label="Primary" value={primaryColor} fallback="#C8A97E" onChange={setPrimaryColor} />
                <ColorField label="Secondary" value={secondaryColor} fallback="#224d89" onChange={setSecondaryColor} />

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Allowed domains
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {internal
                            ? "Apex domains this brand may crawl and audit (e.g. domain.com)."
                            : "Your brand's own website domains. Category and brand imports and their analysis run on these sites (e.g. yourbrand.com)."}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
                        {domains.map((d) => (
                            <Chip
                                key={d}
                                label={d}
                                size="small"
                                onDelete={() => setDomains(domains.filter((x) => x !== d))}
                            />
                        ))}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            value={domainInput}
                            placeholder="domain.com"
                            onChange={(e) => setDomainInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    addDomain();
                                }
                            }}
                        />
                        <Button variant="outlined" size="small" onClick={addDomain}>
                            Add
                        </Button>
                    </Stack>
                </Box>

                {internal && (
                    <TextField
                        label="PDP audit guidance"
                        multiline
                        minRows={4}
                        value={auditGuidance}
                        onChange={(e) => setAuditGuidance(e.target.value)}
                        helperText="Instructions injected into the PDP audit prompt (e.g. how this brand's PDPs are structured or what the audit should weigh)."
                    />
                )}

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Portal login URL
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {portalUrl}
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => {
                                navigator.clipboard.writeText(portalUrl);
                                toast.success("Copied.");
                            }}
                        >
                            Copy
                        </Button>
                    </Stack>
                </Box>

                <Box>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving…" : "Save settings"}
                    </Button>
                </Box>
            </Stack>

            {/* Live preview */}
            <Box className="flex-1">
                <Typography variant="subtitle2" gutterBottom>
                    Preview
                </Typography>
                <Box
                    sx={{
                        border: "1px solid rgba(128,128,128,0.3)",
                        borderRadius: 2,
                        overflow: "hidden",
                        background: preview.background,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            borderBottom: "1px solid rgba(128,128,128,0.25)",
                        }}
                    >
                        {brand.logo && (
                            <Box component="img" src={brand.logo} alt={brand.name} sx={{ height: 28, maxWidth: 90, objectFit: "contain" }} />
                        )}
                        <Typography sx={{ fontWeight: 700, color: preview.primary, fontSize: 15 }}>
                            {preview.systemName}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 2 }}>
                        {brand.logo && (
                            <Box component="img" src={brand.logo} alt={brand.name} sx={{ height: 48, maxWidth: 160, objectFit: "contain" }} />
                        )}
                        <Typography sx={{ fontWeight: 700, color: preview.primary }}>
                            {preview.systemName}
                        </Typography>
                        {preview.loginTagline && (
                            <Typography variant="caption" sx={{ color: contrastText(preview.background), opacity: 0.6 }}>
                                {preview.loginTagline}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                px: 4,
                                py: 1,
                                borderRadius: 2,
                                background: preview.primary,
                                color: contrastText(preview.primary),
                                fontWeight: 600,
                                fontSize: 14,
                            }}
                        >
                            Login
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

// Internal brand detail > Network > White Label.
export function WhiteLabelSettingsPanel() {
    const { brand, setBrand } = useOutletContext<IBrandDetailsContext>();
    return <WhiteLabelSettingsForm brand={brand} setBrand={setBrand} internal />;
}
